import React, { useState, useEffect } from 'react';
import { Link as LinkIcon, Code, LayoutGrid, Rocket, ExternalLink, Plus, Edit3, Trash2, Save, X } from 'lucide-react';
import { MOCK_WEB_APPS } from '../../constants';
import { getWebApps, saveWebApps } from '../../services/homepageContentService';
import { trackButtonClick } from '../../services/analyticsService';
import TokenDisplay from '../common/TokenDisplay';
import RecentUpdatesCard from '../common/RecentUpdatesCard';
import FeedbackFormCard from '../common/FeedbackFormCard';
import AppsIntro from '../common/AppsIntro';

const AppsPage = ({ userData, userId, isAdmin, setMessage }) => {
  const [webApps, setWebApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Icon mapping for dynamic icon rendering
  const iconMap = {
    Code,
    LayoutGrid,
    Rocket,
    ExternalLink,
  };

  const iconOptions = [
    { value: 'Code', label: 'Code', Icon: Code },
    { value: 'LayoutGrid', label: 'Layout Grid', Icon: LayoutGrid },
    { value: 'Rocket', label: 'Rocket', Icon: Rocket },
    { value: 'ExternalLink', label: 'External Link', Icon: ExternalLink },
  ];

  useEffect(() => {
    const loadWebApps = async () => {
      try {
        const data = await getWebApps();
        if (data && data.length > 0) {
          setWebApps(data);
        } else {
          // Fallback to mock data if database is empty
          setWebApps(MOCK_WEB_APPS);
        }
      } catch (error) {
        console.error('Error loading web apps:', error);
        // Fallback to mock data on error
        setWebApps(MOCK_WEB_APPS);
      } finally {
        setLoading(false);
      }
    };
    loadWebApps();
  }, []);

  const handleSave = async () => {
    trackButtonClick({
      id: 'save-apps-button',
      text: 'Save All',
      type: 'admin',
      page: 'apps'
    }, userId);
    
    try {
      setLoading(true);
      // Filter out temporary IDs and ensure all required fields are present
      const appsToSave = webApps
        .filter(app => app.name && app.name.trim() !== '')
        .map(app => ({
          name: app.name.trim(),
          description: app.description || '',
          icon: app.icon || 'Code',
          url: app.url || '#',
        }));
      
      await saveWebApps(appsToSave);
      
      // Reload apps from database to get proper IDs
      const data = await getWebApps();
      if (data && data.length > 0) {
        setWebApps(data);
      } else {
        setWebApps(MOCK_WEB_APPS);
      }
      
      setMessage("Web apps updated successfully!");
      setEditingApp(null);
      setIsAddingNew(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving web apps:', error);
      setMessage("Failed to save web apps. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    trackButtonClick({
      id: 'add-app-button',
      text: 'Add App',
      type: 'admin',
      page: 'apps'
    }, userId);
    
    const newApp = {
      id: `temp-${Date.now()}`,
      name: '',
      description: '',
      icon: 'Code',
      url: '#',
    };
    setWebApps([...webApps, newApp]);
    setEditingApp(newApp.id);
    setIsAddingNew(true);
  };

  const handleEdit = (appId) => {
    const app = webApps.find(a => a.id === appId);
    trackButtonClick({
      id: 'edit-app-button',
      text: 'Edit',
      type: 'admin',
      page: 'apps',
      additionalData: { app_name: app?.name || 'Unknown' }
    }, userId);
    
    setEditingApp(appId);
    setIsAddingNew(false);
  };

  const handleDelete = async (appId) => {
    const app = webApps.find(a => a.id === appId);
    if (window.confirm('Are you sure you want to delete this web app?')) {
      trackButtonClick({
        id: 'delete-app-button',
        text: 'Delete',
        type: 'admin',
        page: 'apps',
        additionalData: { app_name: app?.name || 'Unknown' }
      }, userId);
      const updatedApps = webApps.filter(app => app.id !== appId);
      setWebApps(updatedApps);
      
      if (editingApp === appId) {
        setEditingApp(null);
        setIsAddingNew(false);
      }

      // Save the deletion to the database immediately
      try {
        setLoading(true);
        // Filter out temporary IDs and ensure all required fields are present
        const appsToSave = updatedApps
          .filter(app => app.name && app.name.trim() !== '')
          .map(app => ({
            name: app.name.trim(),
            description: app.description || '',
            icon: app.icon || 'Code',
            url: app.url || '#',
          }));
        
        await saveWebApps(appsToSave);
        
        // Reload apps from database to get proper IDs
        const data = await getWebApps();
        if (data && data.length > 0) {
          setWebApps(data);
        } else {
          setWebApps(MOCK_WEB_APPS);
        }
        
        setMessage("Web app deleted successfully!");
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting web app:', error);
        setMessage("Failed to delete web app. Please try again.");
        // Reload from database to restore original state
        const data = await getWebApps();
        if (data && data.length > 0) {
          setWebApps(data);
        } else {
          setWebApps(MOCK_WEB_APPS);
        }
        setTimeout(() => setMessage(''), 3000);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    trackButtonClick({
      id: 'cancel-edit-app-button',
      text: 'Cancel',
      type: 'admin',
      page: 'apps'
    }, userId);
    
    if (isAddingNew) {
      // Remove the newly added app if canceling
      setWebApps(webApps.filter(app => app.id !== editingApp));
    }
    setEditingApp(null);
    setIsAddingNew(false);
  };

  const handleAppChange = (appId, field, value) => {
    setWebApps(webApps.map(app => 
      app.id === appId ? { ...app, [field]: value } : app
    ));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-10">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-8">
        <AppsIntro isAdmin={isAdmin} setMessage={setMessage} />
        
        <div className="flex justify-between items-center border-b pb-2">
          {isAdmin && !editingApp && (
            <button
              onClick={handleAddNew}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <Plus className="w-4 h-4" />
              <span>Add App</span>
            </button>
          )}
          {isAdmin && editingApp && (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>{loading ? 'Saving...' : 'Save All'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          )}
        </div>
        {loading && !editingApp ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-6 panel">
                <div className="animate-pulse">
                  <div className="h-8 w-8 bg-gray-200 rounded mb-3"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {webApps.map((app, index) => {
              const IconComponent = iconMap[app.icon] || Code;
              const isEditing = editingApp === app.id;
              
              return (
                <div 
                  key={app.id || index} 
                  className={`p-6 panel ${
                    isEditing ? 'ring-2 ring-indigo-500' : 'hover:border-stone-300'
                  }`}
                >
                  {isEditing ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                        <select
                          value={app.icon || 'Code'}
                          onChange={(e) => handleAppChange(app.id, 'icon', e.target.value)}
                          className="field"
                        >
                          {iconOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={app.name || ''}
                          onChange={(e) => handleAppChange(app.id, 'name', e.target.value)}
                          className="field"
                          placeholder="App Name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={app.description || ''}
                          onChange={(e) => handleAppChange(app.id, 'description', e.target.value)}
                          rows="3"
                          className="field"
                          placeholder="App Description"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                        <input
                          type="text"
                          value={app.url || ''}
                          onChange={(e) => handleAppChange(app.id, 'url', e.target.value)}
                          className="field"
                          placeholder="/app-url or https://..."
                        />
                      </div>
                      <button
                        onClick={() => handleDelete(app.id)}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start mb-3">
                        <IconComponent className="w-8 h-8 text-stone-600" />
                        {isAdmin && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(app.id)}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-gray-100 rounded transition"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(app.id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                      <h3 className="font-serif text-2xl font-semibold tracking-tight text-stone-900">{app.name}</h3>
                      <p className="mt-2 text-base text-stone-600 leading-relaxed">{app.description}</p>
                      <a
                        href={app.url}
                        target={app.url.startsWith('#') ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        onClick={() => {
                          trackButtonClick({
                            id: 'launch-app-link',
                            text: 'Launch App',
                            type: 'action',
                            page: 'apps',
                            additionalData: {
                              app_name: app.name,
                              app_url: app.url,
                              is_external: !app.url.startsWith('#')
                            }
                          }, userId);
                        }}
                        className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium transition"
                      >
                        <span>Launch App</span>
                        <LinkIcon className="w-4 h-4 ml-2" />
                      </a>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Token Display */}
        <div className="pt-4 mt-8 border-t border-gray-200">
          <TokenDisplay userData={userData} userId={userId} />
        </div>
      </div>
      
      {/* Sidebar */}
      <aside className="lg:col-span-1 space-y-6">
        <RecentUpdatesCard isAdmin={isAdmin} setMessage={setMessage} />
        <FeedbackFormCard webApps={webApps} setMessage={setMessage} />
      </aside>
    </div>
  );
};

export default AppsPage;
